import { ChevronDownIcon } from '@primer/octicons-react';
import clsx from 'clsx';

import { Text } from '../typography';
import styles from './ContextSelector.module.css';

/** The menu the selector opens, when it opens one. Both halves or neither: see `Props.menu`. */
export interface ContextSelectorMenu {
  /** Whether the menu is currently open, for `aria-expanded`. */
  isOpen: boolean;
  /** Called on click. The call site owns the menu, so it decides whether that opens or closes it. */
  onToggle: () => void;
}

export interface Props {
  /** Text-only identity for the unified investor shell; the default keeps the entity monogram. */
  variant?: 'default' | 'text';
  /**
   * The entity the sidebar's navigation applies to: an organisation on the investor rail, a company
   * on the corporate one. Shown in full in a native `title`, so a name too long for the rail can
   * still be read on hover.
   */
  name: string;
  /**
   * Given, the box is a menu trigger: a caret, a hover ground, a focus ring. Left out, it is a
   * static box with none of those.
   *
   * One optional object rather than an `isOpen` and an `onToggle` that could each be passed alone,
   * because "the caret is part of the menu, not part of the header" is the rule this component
   * exists to hold (`design/40-shell-and-pages.md` "Sidebar"), and a trigger that draws a caret
   * while telling assistive tech nothing about the menu behind it is the way that gets broken. The
   * type makes the pair unsplittable.
   *
   * What decides which variant a viewer gets is a property of the viewer, never a styling choice.
   * The corporate rail keys it on the viewer being a DMA consultant and the investor rail hides the
   * selector outright below two organisations, so both rules stay at their call sites: this
   * component renders the decision, it does not make it.
   */
  menu?: ContextSelectorMenu;
}

/**
 * The context selector at the head of the app shell's sidebar: the entity the navigation below it
 * applies to, and the control that switches entity when the viewer has another one.
 *
 * Specified once in `design/40-shell-and-pages.md` "Sidebar" and, until PLT-6220, built four times
 * over: the investor rail's organisation selector, the corporate rail's company selector, the
 * prototype shell's, and the story-local one inside the `Design System/App shell` specimen that
 * documents the slot. They had drifted on the avatar's colour, the caret's ink and inset, the hover
 * ground and the focus ring (`ContextSelector.module.css` lists which copy differed where), so a
 * rail fix meant finding and measuring all four — including the one a reader would take as the
 * reference.
 *
 * **What it deliberately does not own is the menu.** The two shipped menus are not the same menu and
 * should not be flattened into one: the investor menu filters a search field over the viewer's
 * organisations and offers a "None" row, while the corporate menu is portalled out of the scrolling
 * nav pane, positioned from the trigger's viewport rect (PLT-5961), and its rows carry payment-tier
 * pills, a per-company settings affordance gated on the viewer's access level, and a two-button
 * chin. Those differences are content and permissions, not variants of a component. So each call
 * site keeps its own menu, its own popover positioning and its own access rules, and takes the
 * *box* from here.
 *
 * Nothing about the geometry is a prop, for the usual reason: per-call-site geometry is the drift
 * this replaced.
 */
export function ContextSelector({ name, menu, variant = 'default' }: Props) {
  const identity = (
    <>
      {/* `aria-hidden`: the letter is the first letter of the name beside it, so a screen reader
          reading both says it twice. */}
      {variant === 'default' && (
        <span className={styles.avatar} aria-hidden="true">
          {name.trim().slice(0, 1).toUpperCase()}
        </span>
      )}
      {/*
        `gray-900` stated rather than inherited. It resolves to the same pixels either way today —
        `--fgColor-default` is `--color__gray-900` — but all three copies inherited it, which is why
        none of them had an answer to "what colour is the entity name": the rail's ambient colour is
        not a decision anybody made about this box. The `Design System/App shell` specimen already
        wrote it out; this is that, moved into the component.
      */}
      <Text size="md" weight="medium" color="gray-900" truncate asChild>
        {/* `title`: the name is ellipsised at the rail's width, and the investor rail carried this
            before the box was a component. It is the only way to read a long name back. */}
        <span className={styles.name} title={name}>
          {name}
        </span>
      </Text>
    </>
  );

  if (menu === undefined) {
    return <div className={clsx(styles.root, variant === 'text' && styles.text)}>{identity}</div>;
  }

  return (
    <button
      // Not `submit`, which is a bare `<button>`'s default. Neither shipped copy set it, and the
      // corporate rail is one wizard away from a form that this would have submitted.
      type="button"
      className={clsx(styles.root, styles.trigger, variant === 'text' && styles.text)}
      aria-haspopup="menu"
      aria-expanded={menu.isOpen}
      onClick={menu.onToggle}
    >
      {identity}
      {/* Decoration: the button is the control, and `aria-haspopup` above already says it opens a
          menu. */}
      <ChevronDownIcon size={16} className={styles.caret} aria-hidden="true" />
    </button>
  );
}
